import { queryOptions } from "@tanstack/react-query";
import { apiFetch } from "@/shared/api";
import type {
  CompanyActivity,
  CompanyRoutine,
  CompanySession,
  CompanySessionFilter,
  CompanySpace,
  CompanyStats,
} from "./types";

// All admin endpoints are global — no `X-Space-Id` injection.
// 30 s polling makes the dashboard feel live without spamming the API:
// stats + sessions move noticeably on that cadence; routines/spaces
// barely change but the cost is trivial.

const POLL_MS = 30_000;

async function getJson<T>(path: string): Promise<T> {
  const resp = await apiFetch(path, undefined, { crossSpace: true });
  if (!resp.ok) throw new Error(`Не удалось загрузить ${path}`);
  return resp.json();
}

export const companyStatsQueryOptions = () =>
  queryOptions({
    queryKey: ["admin", "dashboard", "stats"],
    queryFn: () => getJson<CompanyStats>("/api/admin/dashboard/stats"),
    refetchInterval: POLL_MS,
    staleTime: POLL_MS / 2,
  });

export const companyActivityQueryOptions = (days = 14) =>
  queryOptions({
    queryKey: ["admin", "dashboard", "activity", days],
    queryFn: () =>
      getJson<CompanyActivity>(`/api/admin/dashboard/activity?days=${days}`),
    // Daily aggregate — refetch less often than live counters.
    refetchInterval: POLL_MS * 4,
    staleTime: POLL_MS * 2,
  });

export const companySpacesQueryOptions = () =>
  queryOptions({
    queryKey: ["admin", "spaces"],
    queryFn: () => getJson<CompanySpace[]>("/api/admin/spaces"),
    refetchInterval: POLL_MS,
    staleTime: POLL_MS / 2,
  });

export const companySessionsQueryOptions = (
  filter: CompanySessionFilter = "all",
  limit = 20,
) =>
  queryOptions({
    queryKey: ["admin", "sessions", filter, limit],
    queryFn: () =>
      getJson<CompanySession[]>(
        `/api/admin/sessions?status=${filter}&limit=${limit}`,
      ),
    refetchInterval: POLL_MS,
    staleTime: POLL_MS / 2,
  });

export const companyRoutinesQueryOptions = () =>
  queryOptions({
    queryKey: ["admin", "routines"],
    queryFn: () => getJson<CompanyRoutine[]>("/api/admin/routines"),
    refetchInterval: POLL_MS * 4,
    staleTime: POLL_MS * 2,
  });
